import { useCallback } from 'react';
import { ConversationAttachmentEntity } from '../../../types/conversation.types';
import { uploadAttachment } from '../chatUpload';
import { ChatDraft } from './useSendChatMessage';

/**
 * A stand-in attachment pointing at the file still on this machine, so the
 * bubble can show the actual picture from the moment it appears instead of a
 * placeholder for one that is already here.
 *
 * The dimensions are measured rather than guessed: they are what lets the
 * message reserve the right box, and a caption takes its width from the
 * picture. The object URL is released by `ChatImage` when it stops being
 * shown - which is when the server's row replaces this one.
 */
const localPreview = (image: File): Promise<ConversationAttachmentEntity> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(image);
    const probe = new Image();

    const done = (width: number | null, height: number | null) =>
      resolve({
        // Negative, and never read: the optimistic row is reconciled by
        // clientMessageId, and a real id here could collide with a real one.
        id: -1,
        type: 'image',
        url,
        thumbnailUrl: null,
        name: image.name,
        mime: image.type,
        size: image.size,
        width,
        height,
        durationMs: null,
      });

    probe.onload = () => done(probe.naturalWidth, probe.naturalHeight);
    // Undecodable here (heic) still shows the right shape from the fallback box.
    probe.onerror = () => done(null, null);
    probe.src = url;
  });

/**
 * The image editor's send: put the message in the conversation now, upload the
 * (possibly edited) file underneath it.
 *
 * The editor used to hold itself open behind a spinner for the length of the
 * upload, which is the wrong place to wait: the picture is already decided, and
 * the person who pressed Send wants to be back in the conversation looking at
 * it. So the bubble goes up with the local file, carrying its own progress
 * (see `ChatImage`), and the editor closes immediately.
 *
 * Resolves true once the message is on screen - which is as soon as the file
 * has been measured, not when it has been uploaded. A failure from here on is
 * the bubble's to report, exactly like any other send that did not make it.
 */
export const useSendImage = ({
  conversationId,
  send,
}: {
  conversationId: number | string | null;
  send: (draft: ChatDraft) => void;
}) => {
  const sendImage = useCallback(
    async (image: File, caption: string, replyToId?: number) => {
      if (conversationId === null) return false;

      /* Measured before anything else, and the one thing worth waiting for:
         the bubble is drawn at its final size from these dimensions, and a
         caption is laid out from the picture's width - so an unmeasured
         picture would collapse the message it is in. Local work on a file
         already in memory, so "waiting" is a frame or two. */
      const preview = await localPreview(image);

      send({
        body: caption,
        replyToId,
        previewAttachments: [preview],
        // Runs behind the bubble; its failure marks that bubble, not this call.
        prepare: async () => [
          await uploadAttachment(conversationId, image, 'image'),
        ],
      });

      return true;
    },
    [conversationId, send]
  );

  return { sendImage };
};
